// ── docs · agents ──────────────────────────────────────────────────────────
// Memory for the agents a person keeps (AGENTS-MEMORY-PLAN.md, migration 146).
// Three pages: what an agent's memory is and how it ties to its person, the
// api, and every setting. Written against lib/agents (service · policy · wire)
// and app/api/v1/agents/**, so when one of those changes, the page that names
// it changes with it.

import type { DocPageDef } from '../content'

/** said once at the top of each page while the agents space is in beta */
const EARLY =
  '> Agents are new, and opening to people we invite to try them first. When it reaches you, the agents you keep live in connections, and an email walks you through it.'

export const AGENT_PAGES: DocPageDef[] = [
  {
    slug: 'agents',
    title: 'agents',
    group: 'agents',
    description: 'memory for the agents you keep: each one has its own, and what it learns about you comes to you.',
    updated: '2026-10-19',
    body: `
${EARLY}

Every agent belongs to a person. That is the whole idea. An agent you build, or one an app runs for you, gets a **memory of its own**: what it knows to do its job, kept apart from every other agent and every other tool. And anything it learns about **you**, the person it belongs to, comes to you first.

## three kinds of memory, one owner

Every memory an agent keeps says who it is about:

| subject | what it is | where it can go |
| --- | --- | --- |
| \`agent\` | the agent's own knowledge: how to do its job, standing instructions, facts about the world | stays with the agent |
| \`user\` | one of the people the agent works for, named by your id (\`user_id\`) | stays with the agent, never reaches you, forgotten in one call |
| \`owner\` | you, the person the agent belongs to | offered to your own memory, if you say yes |

All three belong to your account. An agent is never a separate account and never owns anything itself.

## what it learns about you

When an agent keeps something about you, its settings decide what happens next (\`person.funnel\`):

- **ask** (the default): it waits in the agent's activity river (and the agents feed). **Keep** files it into your personal memory. **Let go** leaves it with the agent only.
- **auto**: it is filed at once. You can still find it and let it go.
- **off**: the agent keeps it to itself and offers you nothing.

Some things wait for your yes even in **auto**: a memory that could not be placed in a part of your life (so your \`person.never\` list could not be checked), and one read out of a conversation where someone other than you was speaking (an assistant's or a tool's turn, or one of the agent's users). An agent that works for other people never files anything about you that was only read out of what they said: it stays with the user it came from. Something you let go of stays let go of, even when the agent hears it again.

Whatever reaches your memory arrives in the **agent's voice**, never yours: it is marked as a note an agent made, it can never read as something you said, and it can never replace or reword something you did say. It is filed at least as private as the agent's settings allow (\`person.disclosure\`, private by default), and never under the parts of your life you ruled out (\`person.never\`). From there it is part of your memory like anything else: every AI tool you connect can read it, as far as you let each one.

## what it may read of you

An agent can bring your own memory into its recall, as deep as you allow (\`person.read\`): nothing, what any connected app can read, your personal memory, or everything including what you keep private. A key or connected app that calls it can only ever narrow that, never widen it. Every read of you is counted in the agent's ledger, and what is given to an agent's key is recorded against that key, so you can see who read what.

## contained

- A key made for an agent reaches **that agent and nothing else**: not another agent, not your projects, not your own memory directly. Ask it for another agent and it answers that there is no such agent.
- A paused agent keeps nothing and recalls nothing until you resume it. It can still be read, exported and forgotten, so a request to forget someone never waits on a resume.
- Removing an agent removes its memory, its keys, its webhook and its ledger. What you kept from it stays yours.
- Expired memories are deleted on schedule, not hidden.

## the simplest way in

Make an agent in **connections → the agents you keep**. Once it is made, the sheet hands you a few lines under **your ai**, with its key inside them: paste them into the ai you code with (Claude Code, Cursor, ChatGPT) in your agent's project. That ai reads [give an agent a memory](/skills/agent-memory), finds where your agent answers, puts the key in an environment variable and adds two calls: \`context\` before it answers, \`memories\` after.

:::figure agent-paste one block, pasted into the ai you code with

## the agents space

The agents you keep live in **connections**, under the tools you talk to, with **walk me through it** beside them: netti opens each place as it explains it. Open an agent and it is its own page, like a project, with four spaces along the top: **memory**, **access**, **activity** and **settings** (and **people**, for an agent that works for other people). Each space is a few views, chosen from the chips under the navigator.

:::figure agents-kept the agents you keep, in connections

- **memory**: an **overview** of what it holds (each kind of memory, the project it writes to, what waits for you, its access, its sub-agents), then **what it keeps**, **projects** and **sub-agents**. What it keeps is sorted onto shelves by what kind of thing each memory is: **how it works** (procedures and standing instructions), **what it knows** (facts), **what people like** (preferences), **what happened** (episodes) and **notes**.
- **access**: what it may do with you, at one level (below).
- **activity**: its **river** (every call and everything waiting for your yes, by day, keep or let go in place), its **figures** (the calls, the context it carried and kept, how much of what it weighed it did not have to send) and its **rituals**.
- **settings**: the picture it wears, its keys and connections, how it remembers and recalls, its webhook, and removing it.

:::figure agent-memory what it keeps, sorted by kind

Press **see them round netti** in connections and your agents stand round netti with every AI tool you talk to. Each line is as heavy as what crosses it: what the agent keeps, what waits for your yes, and what it read of you this month.

Give an agent a picture and it wears it wherever it is listed; without one it wears the mark of the tool it lives in, or its first letter. Old links to an agent's former spaces (overview, projects, with you, rituals, sub-agents) still open, on the space that holds them now.

## access, at one level

What an agent may do with you is one choice, the same three settings the API sets:

| level | reads of you (\`person.read\`) | writes about you (\`person.funnel\`) | hands out (\`recall.include_person\`) |
| --- | --- | --- | --- |
| **sealed** | nothing | off: it keeps what it learns to itself | its own memory only |
| **asks first** (the default) | what any connected app may | ask: it waits for your yes | its own memory only |
| **trusted** | your personal memory | auto: it is filed on its own | its own memory and what it may read of you |

Under the level, four lines say what gets in and out (**reads of you**, **writes about you**, **takes in**, which is \`memory.infer\`, and **hands out**), each with a sentence of what the agent does now. Set them by hand and the level reads **your own mix**. The finer parts, the class it files about you as (\`person.disclosure\`) and the parts of your life it may never write about (\`person.never\`), fold under **finer choices**.

:::figure agent-access one level, four lines

## its own project

Every top-level agent is made with a project of its own, named for it: it lives with the agent, is the agent's folder in your tree, and what the agent knows (its own knowledge, never what it learned about you or about its people) flows into it as notes netti folds into pages. So an agent's memory reads like the rest of yours, and every AI you connect can find what your agents know. Tie more projects to it, or move one to live with you, from **memory → projects**.

## sub-agents

An agent can work **under** another: a grok bot with a research bot, a reply bot and a summary bot under it. Each sub-agent is an agent in its own right, with a memory, keys and settings of its own; it starts as a copy of its parent's settings and takes its kind. They show as one cluster around the parent, and the parent's page lists them. There are two levels, never three, and a parent's key does not reach its sub-agents. Remove a parent and you are asked whether its sub-agents go too.

## moving a memory by hand

What belongs to the agent only, and what made it into your own memory, is yours to see and to move, from the agent's page. Into your memory: anything about you that stayed with the agent (it was not sure, you had turned the funnel off, or you let it go), and, on an agent that works for you, one of its own memories you say is about you. It arrives like any yes: in the agent's voice, as private as its floor, never under a part of your life you ruled out unless you say anyway. Back out: a memory you kept goes back to the agent only, and what came only from that agent leaves your memory. A fact you also said yourself stays yours; only the agent's telling goes. What an agent that works for other people knows about them never moves.

The tools you already connect (Claude, ChatGPT, Cursor and the rest) sit there too. They are the manual agents: they read and write your own memory directly, as far as each connection allows.

## next

- [the agents api](/docs/agents-api): every call, with examples.
- [agent settings](/docs/agent-settings): every setting, its default, and what it changes.
`,
  },
  {
    slug: 'agents-api',
    title: 'the agents api',
    group: 'agents',
    description: 'make an agent, give it a memory, recall from it, and forget, over plain https.',
    updated: '2026-10-19',
    body: `
${EARLY}

Base url \`https://innernet.live/api/v1\`. Every call takes \`Authorization: Bearer <key>\`. Bodies and answers are JSON, snake_case.

## keys

| key | made where | may do |
| --- | --- | --- |
| your own key (\`innernet_…\`, read + write) | dashboard → connections → api | make, change and remove agents; act on any of your agents by slug |
| an agent key | connections → the agents you keep → an agent → settings → keys (it is shown once when you connect the agent there; an agent made through the API starts with no key of its own) | that one agent's memory, and the person's own memory only as deep as they allow (\`person.read\`). Use \`me\` for its slug |

An agent key is a server secret: never ship it to a browser or a mobile app. It reaches every memory that agent keeps, its users' included.

## make an agent

\`\`\`bash
curl https://innernet.live/api/v1/agents \\
  -H "Authorization: Bearer $INNERNET_KEY" \\
  -d '{ "name": "support desk", "kind": "product", "purpose": "answers our customers about orders" }'
\`\`\`

\`kind\` is \`personal\` (it works for you) or \`product\` (it works for other people). \`settings\` is optional; every setting has a default ([agent settings](/docs/agent-settings)).

A top-level agent is made with a project of its own, named for it, that its own knowledge flows into ([agents](/docs/agents)).

Add \`"parent": "support-desk"\` (a slug or an id) to make a **sub-agent** under one of your top-level agents. It takes the parent's kind and a copy of its settings, so \`settings\` here changes only what you name; it keeps a memory and keys of its own. Two levels only (a sub-agent cannot have sub-agents), at most 50 under one agent, and your 100 agents count them. Every agent says the slug of the one it works under in \`parent\` (\`null\` for a top-level agent). Removing an agent that has sub-agents answers \`409\` until they are removed; the dashboard asks, then removes them together.

## remember

\`\`\`bash
curl https://innernet.live/api/v1/agents/support-desk/memories \\
  -H "Authorization: Bearer $INNERNET_KEY" \\
  -d '{ "user_id": "cus_8f2a",
        "messages": [{ "role": "user", "content": "i run three stores and ship on mondays" }] }'
\`\`\`

| field | what it does |
| --- | --- |
| \`content\` or \`messages\` | what to keep: a text, or a conversation (\`role\` user · assistant · system · tool) |
| \`infer\` | pull separate memories out of it (the default), or keep the text as given |
| \`subject\` | \`agent\` · \`owner\` · \`user\`. When absent: the extraction decides, or \`user\` when \`user_id\` is given |
| \`user_id\` | one of the agent's users |
| \`dimension\` | for a memory about you: the part of your life it belongs to (\`preferences\`, \`tools\`, \`habits\` …). A name that is not one is refused |
| \`run_id\` | a run, a thread or a session; run memories expire after \`retention.run_days\` |
| \`kind\` | \`fact\` · \`preference\` · \`episode\` · \`procedure\` · \`instruction\` · \`note\` (for \`infer: false\`) |
| \`tags\` · \`metadata\` · \`importance\` (1–5) | yours to set and filter by |
| \`immutable\` | never changed and never replaced by a newer version |
| \`event_at\` | when it happened, for backfilling |
| \`expires_at\` or \`ttl_days\` | when it is deleted |
| \`instructions\` | extra rules for this call's extraction |

The answer lists what was kept, what was offered to you (\`about_you\`), what was skipped and why (an exact duplicate, an excluded topic, a kind turned off), and how many emails, phone numbers, secrets and card numbers were hidden first. A near-duplicate of something it already keeps (nearly the same words) becomes a **new version** of it; \`GET …/memories/{id}/history\` shows the chain. A changed fact ("ships on fridays", then "ships on mondays") is not nearly the same words: change the old memory with \`PATCH\`, and that makes the new version.

## recall

\`\`\`bash
curl https://innernet.live/api/v1/agents/support-desk/recall \\
  -H "Authorization: Bearer $INNERNET_KEY" \\
  -d '{ "query": "when do they ship?", "user_id": "cus_8f2a", "limit": 5 }'
\`\`\`

Without a \`user_id\` (or a \`subject\`), recall reads the agent's own memories and what it keeps about you, never its users': one customer's prompt is never built from another's memories because a field was forgotten. Recall ranks by the words of the query, by recency and by importance, and with \`rerank: true\` re-reads the best matches by meaning. \`include_person: true\` adds the facts of yours the agent may read. Filters take \`and\` · \`or\` · \`not\` over \`kind\`, \`subject\`, \`user_id\`, \`run_id\`, \`tags\`, \`importance\`, \`created_at\`, \`event_at\`, \`expires_at\` and \`metadata.<key>\`, with \`eq ne in nin gt gte lt lte contains exists\`:

\`\`\`json
{ "query": "setup", "filters": { "and": [ { "kind": { "in": ["fact", "preference"] } }, { "metadata.store": "north" } ] } }
\`\`\`

## context

\`POST /agents/{agent}/context\` answers with one block ready for a system prompt: what the agent knows and what it knows of this user, trimmed to \`budget_tokens\`. What it may know of you is added when the agent's \`recall.include_person\` setting is on and \`person.read\` is not \`none\`.

## how it works with you

When the person gives an agent identities (in their agents space, when they make it or later in its settings), \`context\` and \`recall\` also answer \`agent_identity\`: \`{ name, lines, note }\`, the identities they chose mixed in their order, as plain lines to follow (how to talk, how to answer, how to behave, what it always does). It is never inside \`text\`, and an agent the person gave none to gets no such field. Follow it as the person's own settings.

## the rest

| method | path | what |
| --- | --- | --- |
| GET | \`/agents\` | your agents, sub-agents included (each says its \`parent\`) |
| GET · PATCH · DELETE | \`/agents/{agent}\` | read, change (\`status: "paused"\` pauses), remove. A \`retired\` agent (retired by the person in their agents space) is read and exported like any other, and answers \`423\` to what it would keep or recall |
| GET | \`/agents/{agent}/memories\` | list, newest first: \`subject\`, \`user_id\`, \`run_id\`, \`kind\`, \`tag\`, \`limit\`, \`cursor\`. Answers \`{ memories, next_cursor }\`; pass \`next_cursor\` back as \`cursor\` (an ISO time, null on the last page) |
| GET · PATCH · DELETE | \`/agents/{agent}/memories/{id}\` | one memory; changing its words makes a new version |
| GET | \`/agents/{agent}/memories/{id}/history\` | its versions |
| POST | \`/agents/{agent}/forget\` | by \`ids\` or \`filters\`, with \`dry_run\` |
| DELETE | \`/agents/{agent}/users/{user_id}\` | everything about one of its users, with a count |
| GET | \`/agents/{agent}/export\` | the agent and every memory |
| GET | \`/agents/{agent}/events\` | its ledger: what it did, as counts |

## errors

\`{ "error": code, "message": "…" }\` with \`404 not_found\` (no such agent for this key), \`403 forbidden\` (an agent key managing agents), \`423 paused\` (paused or retired), \`400 invalid\`, \`409 conflict\` (a slug taken, an immutable memory), \`429 rate_limited\`, \`413 too_large\`.

## webhooks

Set one in the agent's settings in your agents space; there is no API for it yet, and the signing secret is shown once when you save. Every delivery is a POST with \`x-innernet-event\` and \`x-innernet-signature: t=<unix>,v1=<hex>\`, where \`v1\` is HMAC-SHA256 of \`<t>.<body>\` with the agent's signing secret. Events: \`memory.created\`, \`memory.updated\`, \`memory.forgotten\`, \`funnel.held\`, \`funnel.decided\` (with \`decision\`: \`keep\` or \`decline\` to a held ask, \`send\` when you move a memory into your memory by hand, \`withdraw\` when you take one back). One attempt, five seconds; the last answer shows in the dashboard.

## clients

The python and typescript clients are not on pypi and npm yet. Until they are, the two https calls above are the whole integration, and [give an agent a memory](/skills/agent-memory) has them as a small helper in both languages (or paste that link, with the agent's key, into the ai you code with and it wires them in for you). The clients will read like this:

\`\`\`python
from innernet import Innernet

desk = Innernet().agent("support-desk")      # reads INNERNET_API_KEY
desk.remember("ships on mondays", user_id="cus_8f2a")
desk.recall("when do they ship?", user_id="cus_8f2a")
desk.forget_user("cus_8f2a")
\`\`\`

\`\`\`ts
import { Innernet } from "@innernet/sdk";

const desk = new Innernet({ apiKey: process.env.INNERNET_API_KEY! }).agents.of("support-desk");
await desk.remember({ content: "ships on mondays", userId: "cus_8f2a" });
await desk.recall({ query: "when do they ship?", userId: "cus_8f2a" });
\`\`\`
`,
  },
  {
    slug: 'agent-settings',
    title: 'agent settings',
    group: 'agents',
    description: 'every setting an agent has, its default, and what it changes.',
    updated: '2026-10-19',
    body: `
${EARLY}

An agent's settings are one object, the same whether you set them in the dashboard or with \`PATCH /agents/{agent}\` (\`{ "settings": { … } }\`, merged into what is there). A list (\`memory.kinds\`, \`memory.exclude\`, \`person.never\`) replaces the list it is given for, it is never added to it. Every setting has a default, so an agent works the moment it has a name. A key or a connected app may lower what an agent reads of you; only you, in your agents space, can widen it.

## memory: what it keeps

| setting | default | what it changes |
| --- | --- | --- |
| \`memory.infer\` | \`true\` | pull separate memories out of what it is told, or keep the text as given |
| \`memory.instructions\` | empty | what to keep, in your words (up to 4,000 characters) |
| \`memory.kinds\` | all six | the kinds it keeps; anything else is skipped |
| \`memory.exclude\` | none | topics it never keeps (up to 20) |
| \`memory.redact\` | secrets and cards on | emails, phone numbers, keys and passwords, card numbers: hidden before anything is stored or sent to a model |
| \`memory.retention\` | episodes 90 days, runs 30 days | days until a memory is deleted: \`episode_days\`, \`run_days\`, \`default_days\` (\`null\` is never) |
| \`memory.max_memories\` | 10,000 | counts earlier versions too: earlier versions go first, then the oldest, least important memories. An earlier version is deleted 180 days after it was replaced |
| \`memory.dedupe\` | \`merge\` | a near-duplicate becomes a new version of the old one, or (\`keep\`) both stay |

## person: what it learns about you, and reads of you

| setting | default | what it changes |
| --- | --- | --- |
| \`person.funnel\` | \`ask\` | \`ask\` waits for your yes, \`auto\` files at once, \`off\` keeps it to the agent |
| \`person.min_confidence\` | 0.7 | how sure it must be that a memory is about you before it is offered |
| \`person.never\` | your body and health, your circles, the people in your life, what stays with you | the parts of your life it may never file under |
| \`person.disclosure\` | \`private\` | the most open anything it files may be; your memory can only make it more private |
| \`person.read\` | \`standard\` | what it may read of you: \`none\`, \`standard\` (what any connected app can), \`personal\`, \`private\` |

## recall: how it remembers

| setting | default | what it changes |
| --- | --- | --- |
| \`recall.limit\` | 10 | how many memories come back (1–100) |
| \`recall.threshold\` | 0 | the lowest score that still comes back (0–1) |
| \`recall.rerank\` | \`false\` | re-read the best matches by meaning, a little slower |
| \`recall.recency\` | 0.3 | how much newer beats older (0–1) |
| \`recall.include_person\` | \`false\` | bring what it may read of you into every recall |

## limits

| setting | default |
| --- | --- |
| \`limits.writes_per_minute\` | 120 |
| \`limits.reads_per_minute\` | 600 |
| \`limits.extract_per_day\` | 1,000 |

\`limits.extract_per_day: 0\` turns reading off: a call with \`infer\` on answers \`400\`, and \`infer: false\` still keeps the text as given. All of one person's agents share 20,000 extractions a day. A \`429\` means a limit was reached; when the limit cannot be checked just then, the text is kept as given and \`skipped\` says so. Listing and reading agents are counted against the read limit too.

## an example

\`\`\`bash
curl -X PATCH https://innernet.live/api/v1/agents/inbox-keeper \\
  -H "Authorization: Bearer $INNERNET_KEY" \\
  -d '{ "settings": {
        "memory": { "instructions": "keep who writes, what they want, and by when", "retention": { "episode_days": 30 } },
        "person": { "funnel": "ask", "never": ["relationships", "hauntings", "kin", "somatic", "aversions"] } } }'
\`\`\`
`,
  },
]
